import { useEffect, useState } from "react";
import { useNavigate } from "@/lib/react-router-dom-shim";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, ShoppingBag, Calendar, MapPin, Eye, Wallet } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useTranslation } from "react-i18next";
import { usePrice } from "@/hooks/usePrice";

interface Reservation {
  id: string;
  reservation_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  status: string;
  total_amount_eur: number;
  created_at: string;
  notes: string | null;
}

interface ReservationItem {
  id: string;
  quantity: number;
  price_eur: number;
  booking_details: any;
  products: {
    name_es: string;
    description_es: string | null;
    product_type: string;
    location: string | null;
    images: any;
  };
}

export default function MyReservations() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { format: formatPrice } = usePrice();
  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [reservationItems, setReservationItems] = useState<ReservationItem[]>([]);
  const [activeTab, setActiveTab] = useState("all");

  const getDateLocale = () => {
    const lang = i18n.language;
    if (lang === 'pt-BR' || lang === 'pt') return 'pt-BR';
    if (lang === 'en') return 'en-US';
    return 'es-ES';
  };

  useEffect(() => {
    checkAuthAndLoadReservations();
  }, []);

  const checkAuthAndLoadReservations = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/auth");
        return;
      }

      await loadReservations(session.user.id);
    } catch (error) {
      console.error("Error:", error);
      navigate("/auth");
    }
  };

  const loadReservations = async (userId: string) => {
    setLoading(true);
    const { data } = await supabase
      .from("reservations")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    setReservations(data || []);
    setLoading(false);
  };

  const loadReservationDetails = async (reservationId: string) => {
    const { data } = await supabase
      .from("reservation_items")
      .select(`
        *,
        products (
          name_es,
          description_es,
          product_type,
          location,
          images
        )
      `)
      .eq("reservation_id", reservationId);

    setReservationItems(data || []);
  };

  const handleViewDetails = async (reservation: Reservation) => {
    setSelectedReservation(reservation);
    await loadReservationDetails(reservation.id);
  };

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      pending_verification: "secondary",
      verified: "default",
      confirmed: "default",
      completed: "default",
      cancelled: "destructive",
    };
    return variants[status] || "secondary";
  };

  const getStatusLabel = (status: string) => {
    return t(`myReservations.status.${status}`);
  };

  const getStatusDescription = (status: string) => {
    return t(`myReservations.statusDescription.${status}`);
  };

  const filteredReservations = reservations.filter((r) => {
    if (activeTab === "all") return true;
    if (activeTab === "active") {
      return ["pending_verification", "verified", "confirmed"].includes(r.status);
    }
    if (activeTab === "past") {
      return ["completed", "cancelled"].includes(r.status);
    }
    return true;
  });

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container mx-auto py-12 flex justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
        <Footer />
      </>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="container mx-auto py-12 max-w-6xl flex-1">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">{t('myReservations.title')}</h1>
        <p className="text-muted-foreground">
          {t('myReservations.subtitle')}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="all">{t('myReservations.tabs.all')}</TabsTrigger>
          <TabsTrigger value="active">{t('myReservations.tabs.active')}</TabsTrigger>
          <TabsTrigger value="past">{t('myReservations.tabs.history')}</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {filteredReservations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium mb-2">{t('myReservations.noReservations')}</p>
                <p className="text-sm text-muted-foreground mb-6">
                  {t('myReservations.noReservationsDescription')}
                </p>
                <Button onClick={() => navigate("/")}>
                  {t('myReservations.exploreDestinations')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredReservations.map((reservation) => (
                <Card key={reservation.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="space-y-3 flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-lg font-semibold">
                            {reservation.reservation_number}
                          </h3>
                          <Badge variant={getStatusVariant(reservation.status)}>
                            {getStatusLabel(reservation.status)}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(reservation.created_at).toLocaleDateString(getDateLocale(), {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </div>
                          <div className="flex items-center gap-1">
                            <Wallet className="h-4 w-4" />
                            {formatPrice(Number(reservation.total_amount_eur))}
                          </div>
                        </div>

                        <p className="text-sm text-muted-foreground">
                          {getStatusDescription(reservation.status)}
                        </p>
                      </div>

                      <Button
                        variant="outline"
                        onClick={() => handleViewDetails(reservation)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {t('myReservations.viewDetails')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedReservation} onOpenChange={() => setSelectedReservation(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('myReservations.reservationDetails')}</DialogTitle>
          </DialogHeader>

          {selectedReservation && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    {t('myReservations.reservationNumber')}
                  </p>
                  <p className="font-semibold">
                    {selectedReservation.reservation_number}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t('myReservations.statusLabel')}</p>
                  <Badge variant={getStatusVariant(selectedReservation.status)}>
                    {getStatusLabel(selectedReservation.status)}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t('myReservations.date')}</p>
                  <p>
                    {new Date(selectedReservation.created_at).toLocaleDateString(getDateLocale(), {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t('common.total')}</p>
                  <p className="text-xl font-bold">
                    {formatPrice(Number(selectedReservation.total_amount_eur))}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">{t('myReservations.reservedProducts')}</h3>
                <div className="space-y-4">
                  {reservationItems.map((item) => (
                    <Card key={item.id}>
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          {item.products.images?.[0] && (
                            <img
                              src={item.products.images[0]}
                              alt={item.products.name_es}
                              className="w-24 h-24 object-cover rounded-lg"
                            />
                          )}
                          <div className="flex-1">
                            <h4 className="font-semibold mb-1">
                              {item.products.name_es}
                            </h4>
                            {item.products.location && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                                <MapPin className="h-3 w-3" />
                                {item.products.location}
                              </div>
                            )}
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-muted-foreground">
                                {t('common.type')}:{" "}
                                <span className="capitalize">
                                  {item.products.product_type}
                                </span>
                              </span>
                              <span className="text-muted-foreground">
                                {t('common.quantity')}: {item.quantity}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">
                              {formatPrice(Number(item.price_eur) * item.quantity)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {formatPrice(Number(item.price_eur))} × {item.quantity}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {selectedReservation.notes && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                  <p className="text-sm font-medium mb-1">{t('myReservations.notes')}</p>
                  <p className="text-sm">{selectedReservation.notes}</p>
                </div>
              )}

              <div className="text-sm text-muted-foreground">
                <p>
                  {t('myReservations.needHelp')}{" "}
                  <a href="mailto:support@example.com" className="text-primary hover:underline">
                    {t('myReservations.contactUs')}
                  </a>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
      <Footer />
    </div>
  );
}
